/**
 * Build breadcrumb string from folder path.
 *
 * @param {Array} folders - All folders in the source package
 * @param {Array} path - Current folder path (array of folder IDs)
 * @returns {string} Breadcrumb (e.g., "Folder 1 / Folder 2" or "Root")
 */
export const buildBreadcrumb = (folders, path) => {
    if (!path || path.length === 0) return 'Root';

    const nameById = new Map(folders.map(f => [f.id, f.name || 'Untitled']));
    return path.map(id => nameById.get(id) || '(unknown)').join(' / ');
};

/**
 * Collect all descendant folder IDs for a given folder.
 * Operates on a **plain JS array** of folder objects (as returned by `getAllFolders`).
 *
 * NOTE: There is a parallel implementation `getDescendantFolderIds(ySourcePackage, folderId)`
 * in `sourcePackageYMap.js` that operates on the live Y.Map. Prefer that version when you
 * have access to `ySourcePackage`; use this one when working with a pre-fetched array
 * (e.g. inside React components that receive `folders` as a prop).
 *
 * Used to prevent illegal move targets (can't move a folder into itself or its descendants).
 *
 * @param {Array} folders - All folders (plain objects)
 * @param {string} folderId - Starting folder ID
 * @returns {Set<string>} Set of descendant folder IDs (does NOT include folderId itself)
 */
export const collectDescendantFolderIds = (folders, folderId) => {
    const descendants = new Set();

    const collect = (parentId) => {
        const children = folders.filter(f => f.parentFolderId === parentId);
        for (const child of children) {
            descendants.add(child.id);
            collect(child.id);
        }
    };

    collect(folderId);
    return descendants;
};

/**
 * Filter folders by parent and sort by position.
 *
 * @param {Array} folders - All folders (plain objects from Yjs, each has a `position` field)
 * @param {string|null} parentId - Parent folder ID (null for root)
 * @returns {Array} Filtered and sorted folders
 */
export const getFoldersAtLevel = (folders, parentId) => {
    return folders
        .filter(f => (f.parentFolderId || null) === (parentId || null))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
};

/**
 * Filter items by folder and sort by position.
 *
 * @param {Array} items - All items
 * @param {string|null} folderId - Folder ID (null for root)
 * @returns {Array} Filtered and sorted items
 */
export const getItemsAtLevel = (items, folderId) => {
    return items
        .filter(it => (it.parentFolderId || null) === (folderId || null))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
};

/**
 * Count items in each folder, keyed by folderId.
 *
 * NOTE: Root-level items (parentFolderId === null) are intentionally excluded;
 * they are not "in" any folder so no folder badge should show a count for them.
 *
 * @param {Array} items - All items (plain objects from Yjs)
 * @returns {Map<string, number>} Map of folderId → item count (only non-root folders)
 */
export const countItemsByFolder = (items) => {
    const counts = new Map();
    for (const item of items) {
        const folderId = item.parentFolderId || null;
        if (folderId !== null) {
            counts.set(folderId, (counts.get(folderId) || 0) + 1);
        }
    }
    return counts;
};
